/**
 * The final report's standard text and equipment list in Teslyn Cloud (MB's decision 885; docs/specs/project-
 * reporting/final-report.md §4; help `cloud.final-report-text`). On Company, "Final report": the company's text, which
 * every project's final report prints. On a project's "Final report" tab: the company's text it prints, or the
 * project's own, which then replaces the company's for that project only ("Use the company's text again" removes it).
 * Tenant admins, managers and data processors write them (`canEditFinalReportText`; the database decides); other
 * staff read them. Teslyn Field receives them when it syncs and prints them in "Generate final report".
 *
 * Thomson Airborne may start from the previous reporting program's own wording (`previousProgramWording`): it fills the
 * form, and is kept only when saved (decisions 885 and 886: never printed by default).
 *
 * Its blanks (`[fill in: …]`) stay in the stored text; when the final report is printed, Teslyn fills the ones it
 * knows from the project (decision 898: the QC limits and the base station's model). Under the text and in the form,
 * `BlankPlanNote` says which blanks are filled from the project (on a project's tab, with its figures) and which stay
 * blank.
 */
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  blankPlan,
  canEditFinalReportText,
  EMPTY_FINAL_REPORT_TEXT,
  PREVIOUS_WORDING_SURVEY_LABEL,
  previousProgramWording,
  previousWordingOffered,
  projectFigures,
  STANDARD_TEXT_PART_LABEL,
  STANDARD_TEXT_PARTS,
  tidyFinalReportText,
  validateFinalReportText,
  type EquipmentItem,
  type FinalReportText,
  type FinalReportTextRow,
  type PreviousWordingSurvey,
  type ProjectFigures,
  type StandardSection,
  type StandardTextPart,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { SelectField, TextArea, TextField } from '../components/Fields.tsx';
import { ErrorState, Loading } from '../components/States.tsx';
import { finalReportTextApi, type FinalReportTextApi } from './api.ts';

const dmy = (iso: string): string => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;

const live = (r: FinalReportTextRow | null): FinalReportTextRow | null =>
  r && r.deleted_at === null ? r : null;

const textOf = (r: FinalReportTextRow | null): FinalReportText =>
  r ? { sections: r.sections, equipment: r.equipment } : EMPTY_FINAL_REPORT_TEXT;

const isEmpty = (t: FinalReportText): boolean => t.sections.length === 0 && t.equipment.length === 0;

function savedLine(r: FinalReportTextRow): string {
  return `Saved${r.changed_by_name ? ` by ${r.changed_by_name}` : ''} on ${dmy(r.updated_at)}.`;
}

function move<T>(list: readonly T[], i: number, by: -1 | 1): T[] {
  const j = i + by;
  if (j < 0 || j >= list.length) return [...list];
  const out = [...list];
  [out[i], out[j]] = [out[j]!, out[i]!];
  return out;
}

/** The text read only: its paragraphs by where they print, and the equipment list. */
export function FinalReportTextView({ text }: { readonly text: FinalReportText }): ReactElement {
  if (isEmpty(text)) return <p className="body muted">Nothing is written yet.</p>;
  return (
    <div className="stack">
      {STANDARD_TEXT_PARTS.map((part) => {
        const sections = text.sections.filter((s) => s.part === part);
        return sections.length === 0 ? null : (
          <section key={part} className="stack" aria-label={STANDARD_TEXT_PART_LABEL[part]}>
            <h3 className="label">{STANDARD_TEXT_PART_LABEL[part]}</h3>
            {sections.map((s, i) => (
              <div key={i}>
                <p className="body">
                  <strong>{s.heading}</strong>
                </p>
                <p className="body pre-line">{s.text}</p>
              </div>
            ))}
          </section>
        );
      })}
      {text.equipment.length > 0 && <EquipmentTable equipment={text.equipment} />}
    </div>
  );
}

function EquipmentTable({ equipment }: { readonly equipment: readonly EquipmentItem[] }): ReactElement {
  return (
    <table className="table" aria-label="Equipment list">
      <thead>
        <tr>
          <th>Instrument</th>
          <th>Model</th>
          <th>Serial number</th>
        </tr>
      </thead>
      <tbody>
        {equipment.map((e, i) => (
          <tr key={i}>
            <td>{e.instrument}</td>
            <td>{e.model}</td>
            <td className="data">{e.serial}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * What the printed report does with each blank of the text (decision 898): filled from the project (with the
 * project's figure on its tab; "from each project" for the company's text), or left blank. Nothing when the text has
 * no blank.
 */
export function BlankPlanNote({
  text,
  figures,
}: {
  readonly text: FinalReportText;
  /** The project's figures on its tab; null for the company's text. */
  readonly figures: ProjectFigures | null;
}): ReactElement | null {
  const plan = blankPlan(text, figures);
  if (plan.length === 0) return null;
  const filled = plan.filter((b) => b.fill !== 'blank');
  const blank = plan.filter((b) => b.fill === 'blank');
  const n = (k: number, one: string, many: string): string => (k === 1 ? `1 ${one}` : `${k} ${many}`);
  return (
    <div className="stack">
      {filled.length > 0 && (
        <div className="notice stack" role="note" aria-label="Filled from the project">
          <p className="body">
            {n(filled.length, 'blank is', 'blanks are')} filled from{' '}
            {figures === null ? 'each project' : 'the project'} when the final report is printed; the saved
            text keeps {filled.length === 1 ? 'it' : 'them'}, so it serves every project:
          </p>
          <ul className="issues">
            {filled.map((b, i) => (
              <li key={i} className="caption">
                {b.where}: {b.blank}
                {b.value !== null && (
                  <>
                    {' '}
                    prints as <strong>{b.value}</strong>
                  </>
                )}
                . {b.from}
              </li>
            ))}
          </ul>
        </div>
      )}
      {blank.length > 0 && (
        <div className="notice notice--warn stack" role="note" aria-label="Blanks that stay blank">
          <p className="body">
            {n(blank.length, 'blank stays blank', 'blanks stay blank')}: the final report prints{' '}
            {blank.length === 1 ? 'it' : 'them'} as {blank.length === 1 ? 'it is' : 'they are'} until you
            replace {blank.length === 1 ? 'it' : 'them'}:
          </p>
          <ul className="issues">
            {blank.map((b, i) => (
              <li key={i} className="caption">
                {b.where}: {b.blank}. {b.from}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** The form: paragraphs (heading, where it prints, text) and the equipment list, each in order. */
export function FinalReportTextEditor({
  value,
  onChange,
  tenantSlug,
  figures = null,
}: {
  readonly value: FinalReportText;
  readonly onChange: (t: FinalReportText) => void;
  /** Thomson Airborne is offered the previous reporting program's wording. */
  readonly tenantSlug: string;
  /** The project's figures on its tab (decision 898); null for the company's text. */
  readonly figures?: ProjectFigures | null;
}): ReactElement {
  const [survey, setSurvey] = useState<PreviousWordingSurvey>('magnetics_radiometrics');
  const [confirm, setConfirm] = useState(false);
  const setSection = (i: number, s: Partial<StandardSection>): void =>
    onChange({ ...value, sections: value.sections.map((x, j) => (j === i ? { ...x, ...s } : x)) });
  const setItem = (i: number, e: Partial<EquipmentItem>): void =>
    onChange({ ...value, equipment: value.equipment.map((x, j) => (j === i ? { ...x, ...e } : x)) });
  const start = (): void => {
    onChange(previousProgramWording(survey));
    setConfirm(false);
  };

  return (
    <div className="stack">
      {previousWordingOffered(tenantSlug) && (
        <fieldset className="fieldset stack" aria-label="Starting text">
          <legend className="label">Starting text</legend>
          <p className="caption muted">
            The previous reporting program&apos;s own final report wording, as a start: its paragraphs and the
            instruments it names, with blanks where it filled in the job&apos;s values. Check every model
            against the equipment flown. Nothing is kept until you press Save.
          </p>
          <div className="row row--wrap">
            <SelectField
              label="Survey"
              value={survey}
              onChange={setSurvey}
              options={Object.entries(PREVIOUS_WORDING_SURVEY_LABEL) as [PreviousWordingSurvey, string][]}
            />
            <Button onClick={() => (isEmpty(value) ? start() : setConfirm(true))}>
              Start from the previous reporting program&apos;s wording
            </Button>
          </div>
          {confirm && (
            <div className="notice notice--warn stack" role="alertdialog" aria-label="Replace what is typed">
              <p className="body">This replaces every paragraph and instrument typed here.</p>
              <div className="row">
                <Button variant="primary" onClick={start}>
                  Replace what is typed
                </Button>
                <Button variant="quiet" onClick={() => setConfirm(false)}>
                  Keep what is typed
                </Button>
              </div>
            </div>
          )}
        </fieldset>
      )}

      <fieldset className="fieldset stack" aria-label="Standard paragraphs">
        <legend className="label">Standard paragraphs</legend>
        <p className="caption muted">
          A blank line starts a new paragraph; a line starting with &quot;- &quot; is a bullet point.
        </p>
        {value.sections.length === 0 && <p className="body muted">No paragraph yet.</p>}
        {value.sections.map((s, i) => (
          <div
            key={i}
            className="stack final-report-text__item"
            role="group"
            aria-label={`Paragraph ${i + 1}`}
          >
            <div className="row row--wrap">
              <TextField
                label={`Heading of paragraph ${i + 1}`}
                value={s.heading}
                onChange={(heading) => setSection(i, { heading })}
              />
              <SelectField<StandardTextPart>
                label="Prints in"
                value={s.part}
                onChange={(part) => setSection(i, { part })}
                options={STANDARD_TEXT_PARTS.map((p) => [p, STANDARD_TEXT_PART_LABEL[p]] as const)}
              />
            </div>
            <TextArea
              label={`Text of paragraph ${i + 1}`}
              value={s.text}
              rows={6}
              onChange={(text) => setSection(i, { text })}
            />
            <div className="row">
              <Button
                variant="quiet"
                disabled={i === 0}
                onClick={() => onChange({ ...value, sections: move(value.sections, i, -1) })}
              >
                Move up
              </Button>
              <Button
                variant="quiet"
                disabled={i === value.sections.length - 1}
                onClick={() => onChange({ ...value, sections: move(value.sections, i, 1) })}
              >
                Move down
              </Button>
              <Button
                variant="quiet"
                onClick={() => onChange({ ...value, sections: value.sections.filter((_, j) => j !== i) })}
              >
                Remove paragraph {i + 1}
              </Button>
            </div>
          </div>
        ))}
        <div className="row">
          <Button
            onClick={() =>
              onChange({
                ...value,
                sections: [...value.sections, { heading: '', text: '', part: 'specifications' }],
              })
            }
          >
            Add paragraph
          </Button>
        </div>
      </fieldset>

      <fieldset className="fieldset stack" aria-label="Equipment list">
        <legend className="label">Equipment list</legend>
        <p className="caption muted">
          Each instrument with its make and model, and its serial number where it matters. The serial number
          column prints only when an instrument has one.
        </p>
        {value.equipment.length === 0 && <p className="body muted">No instrument yet.</p>}
        {value.equipment.map((e, i) => (
          <div key={i} className="row row--wrap" role="group" aria-label={`Equipment row ${i + 1}`}>
            <TextField
              label={`Instrument ${i + 1}`}
              value={e.instrument}
              onChange={(instrument) => setItem(i, { instrument })}
            />
            <TextField label="Model" value={e.model} onChange={(model) => setItem(i, { model })} />
            <TextField
              label="Serial number"
              value={e.serial}
              spellCheck={false}
              onChange={(serial) => setItem(i, { serial })}
            />
            <Button
              variant="quiet"
              disabled={i === 0}
              onClick={() => onChange({ ...value, equipment: move(value.equipment, i, -1) })}
            >
              Move up
            </Button>
            <Button
              variant="quiet"
              onClick={() => onChange({ ...value, equipment: value.equipment.filter((_, j) => j !== i) })}
            >
              Remove instrument {i + 1}
            </Button>
          </div>
        ))}
        <div className="row">
          <Button
            onClick={() =>
              onChange({
                ...value,
                equipment: [...value.equipment, { instrument: '', model: '', serial: '' }],
              })
            }
          >
            Add instrument
          </Button>
        </div>
      </fieldset>

      <BlankPlanNote text={value} figures={figures} />
    </div>
  );
}

export interface FinalReportTextPanelProps {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly tenantSlug: string;
  /** Null: the company's text (Company, "Final report"). Otherwise the project's tab. */
  readonly projectId: string | null;
  readonly roles: readonly string[];
  /** Tests pass their own. */
  readonly api?: FinalReportTextApi;
}

export function FinalReportTextPanel({
  sb,
  tenantId,
  tenantSlug,
  projectId,
  roles,
  api: given,
}: FinalReportTextPanelProps): ReactElement {
  const api = useMemo(() => given ?? finalReportTextApi(sb), [given, sb]);
  const may = canEditFinalReportText(roles);
  const [company, setCompany] = useState<FinalReportTextRow | null>(null);
  const [own, setOwn] = useState<FinalReportTextRow | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<FinalReportText | null>(null);
  const [problems, setProblems] = useState<readonly string[]>([]);
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [figures, setFigures] = useState<ProjectFigures | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [c, p, qc] = await Promise.all([
        api.load(tenantId, null),
        projectId === null ? Promise.resolve(null) : api.load(tenantId, projectId),
        // the project's QC settings fill the blanks (decision 898); unread, the tab says "from the project"
        projectId === null ? Promise.resolve(null) : api.projectQc(projectId).catch(() => null),
      ]);
      setCompany(c);
      setOwn(p);
      // Teslyn Cloud does not keep the base station files: the laptop that prints fills the model
      setFigures(qc ? projectFigures({ project: qc, baseStations: null }) : null);
      setLoaded(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [api, tenantId, projectId]);
  useEffect(() => {
    void load();
  }, [load]);

  // the row this view writes: the company's, or the project's own
  const target = projectId === null ? company : own;
  const ownLive = projectId === null ? null : live(own);
  const editing = draft !== null;

  async function save(): Promise<void> {
    if (!draft) return;
    const text = tidyFinalReportText(draft);
    const issues = validateFinalReportText(text);
    setProblems(issues);
    if (issues.length > 0) return;
    setBusy(true);
    try {
      const row = await api.save(tenantId, projectId, target, text);
      if (projectId === null) setCompany(row);
      else setOwn(row);
      setDraft(null);
      setDone(
        `${projectId === null ? "The company's" : "This project's own"} standard text is saved. Teslyn Field ` +
          'laptops receive it when they next sync.',
      );
    } catch (e) {
      setProblems([e instanceof Error ? e.message : String(e)]);
    } finally {
      setBusy(false);
    }
  }

  async function backToCompany(): Promise<void> {
    if (!ownLive) return;
    setBusy(true);
    try {
      setOwn(await api.remove(ownLive.id));
      setDraft(null);
      setDone("This project prints the company's standard text again.");
    } catch (e) {
      setProblems([e instanceof Error ? e.message : String(e)]);
    } finally {
      setBusy(false);
    }
  }

  if (error) return <ErrorState message={error} onRetry={() => void load()} />;
  if (!loaded) return <Loading what="the final report's standard text" />;

  const companyLive = live(company);
  const shown = projectId === null ? textOf(companyLive) : ownLive ? textOf(ownLive) : textOf(companyLive);

  return (
    <div className="stack" role="region" aria-label="Final report standard text">
      <p className="body">
        {projectId === null
          ? 'The standard paragraphs and the equipment list every final report prints, unless a project has its own. ' +
            'Teslyn Field prints them in "Generate final report".'
          : ownLive
            ? "This project has its own standard text: its final report prints this instead of the company's."
            : companyLive
              ? 'This project\'s final report prints the company\'s standard text (Company, "Final report").'
              : 'Nothing is written for the company or this project: the final report prints without it.'}
      </p>
      {(projectId === null ? companyLive : (ownLive ?? companyLive)) && (
        <p className="caption muted">
          {savedLine((projectId === null ? companyLive : (ownLive ?? companyLive))!)}
        </p>
      )}
      {done && (
        <p className="notice body" role="status">
          {done}
        </p>
      )}
      {problems.length > 0 && (
        <div className="notice notice--fail" role="alert">
          <ul className="issues">
            {problems.map((p) => (
              <li key={p} className="body">
                {p}
              </li>
            ))}
          </ul>
        </div>
      )}

      {editing ? (
        <>
          <FinalReportTextEditor
            value={draft}
            onChange={setDraft}
            tenantSlug={tenantSlug}
            figures={figures}
          />
          <div className="row">
            <Button variant="primary" busy={busy} onClick={() => void save()}>
              Save
            </Button>
            <Button
              variant="quiet"
              onClick={() => {
                setDraft(null);
                setProblems([]);
              }}
            >
              Cancel
            </Button>
          </div>
        </>
      ) : (
        <>
          <FinalReportTextView text={shown} />
          <BlankPlanNote text={shown} figures={figures} />
          {may ? (
            <div className="row row--wrap">
              {projectId === null || ownLive ? (
                <Button
                  variant="primary"
                  onClick={() => {
                    setDone(null);
                    setDraft(shown);
                  }}
                >
                  {isEmpty(shown) ? 'Write the standard text' : 'Edit'}
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={() => {
                    setDone(null);
                    setDraft(textOf(companyLive));
                  }}
                >
                  Write this project&apos;s own text
                </Button>
              )}
              {ownLive && (
                <Button busy={busy} onClick={() => void backToCompany()}>
                  Use the company&apos;s text again
                </Button>
              )}
            </div>
          ) : (
            <p className="caption muted">
              Tenant admins, managers and data processors write the final report&apos;s standard text.
            </p>
          )}
        </>
      )}
    </div>
  );
}
